import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { setWebTitle, tagColor } from '@/lib/format-utils';
import { usePersons, useDeletePerson } from '@/hooks/api-hooks';
import { ShowPerson, SkeletonList, PaxCategorySelect } from '@/components/snippets';
import { notify } from '@/lib/notify';
import { Link, useSearchParams } from 'react-router-dom';
import { PERSONS_PATH } from '@/lib/navigation';
import { Badge, Button, Checkbox } from '@mantine/core';
import { IconId, IconTrash, IconUsers } from '@tabler/icons-react';
import { PersonCategory } from '@turizi/shared';
import { PersonToString } from '@/lib/models-utils';
import SearchInput from '@/components/search-input';

export default function PersonsPage(): JSX.Element {
    const { t } = useTranslation();
    const {
        isLoading,
        data
    } = usePersons();
    const persons = data?.data || [];
    const deletePerson = useDeletePerson();

    setWebTitle(t('persons'));

    const [searchParams, setSearchParams] = useSearchParams();
    const searchText = searchParams.get('search') || '';
    const [categoryFilter, setCategoryFilter] = useState<PersonCategory | null>(null);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleSearch = (val: string) => {
        const newParams = new URLSearchParams(searchParams);
        if (val) {
            newParams.set('search', val);
        } else {
            newParams.delete('search');
        }
        setSearchParams(newParams, { replace: true });
    };

    useEffect(() => {
        setSelectedIds([]);
    }, [searchText, categoryFilter]);

    useEffect(() => {
        setSelectedIds(prev => prev.filter(id => persons.some(p => p.id === id)));
    }, [persons]);

    const filteredPersons = useMemo(() => {
        return persons
            .filter(person => {
                if (categoryFilter && person.category !== categoryFilter) {
                    return false;
                }
                if (!searchText) return true;
                return PersonToString(person, t).toLowerCase().includes(searchText.toLowerCase());
            })
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [persons, searchText, categoryFilter, t]);

    const handleDeleteSelected = async (ids: number[]) => {
        if (confirm(t('confirm_delete'))) {
            setIsDeleting(true);
            try {
                await Promise.all(ids.map(id => deletePerson.mutateAsync(id)));
                setSelectedIds([]);
                notify.success('operation.success', 'operation.success');
            } catch (error) {
                console.error('Failed to delete selected persons:', error);
                notify.error('operation.error', 'operation.error');
            } finally {
                setIsDeleting(false);
            }
        }
    };

    const allSelected = filteredPersons.length > 0 && selectedIds.length === filteredPersons.length;
    const someSelected = selectedIds.length > 0 && selectedIds.length < filteredPersons.length;

    return (
        <div>
            <div className="panel-content space-y-3">
                {/* Search, Filter & Bulk Actions Bar */}
                <div className="flex flex-wrap items-center gap-2 sticky top-15 z-20 bg-slate-50/90 backdrop-blur-xs py-1">
                    <div className="flex items-center pl-2.5 pr-1">
                        <Checkbox
                            size="xs"
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={(event) => {
                                if (event.currentTarget.checked) {
                                    setSelectedIds(filteredPersons.map(p => p.id));
                                } else {
                                    setSelectedIds([]);
                                }
                            }}
                        />
                    </div>

                    <div className="flex-1 min-w-[220px]">
                        <SearchInput
                            size="sm"
                            onSearch={handleSearch}
                            initialValue={searchText}
                            autoFocus={true}
                        />
                    </div>

                    <div className="w-40">
                        <PaxCategorySelect
                            size="sm"
                            value={categoryFilter}
                            onChange={setCategoryFilter}
                            clearable
                        />
                    </div>

                    <span className="text-xs font-mono text-slate-500 bg-white border border-slate-200/80 px-2.5 py-1.5 rounded-lg">
                        {filteredPersons.length}
                    </span>

                    {selectedIds.length > 0 && (
                        <Button
                            color="red"
                            variant="filled"
                            size="xs"
                            radius="xl"
                            loading={isDeleting}
                            leftSection={<IconTrash size={13} />}
                            onClick={() => handleDeleteSelected(selectedIds)}
                        >
                            {t('delete_selected')} ({selectedIds.length})
                        </Button>
                    )}
                </div>

                {/* Persons List */}
                {isLoading ? (
                    <SkeletonList />
                ) : filteredPersons.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-10 bg-white rounded-xl border border-slate-200/80 text-center space-y-2">
                        <IconUsers size={28} className="text-slate-300" />
                        <span className="text-xs text-slate-400">{t('no_results')}</span>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl border border-slate-200/80 divide-y divide-slate-100 overflow-hidden shadow-2xs">
                        {filteredPersons.map(person => {
                            const isSelected = selectedIds.includes(person.id);
                            const docStr = [
                                person.document_type ? t(person.document_type) : '',
                                person.document_number
                            ].filter(Boolean).join(' ');

                            return (
                                <div
                                    key={person.id}
                                    className={`flex items-center gap-2.5 px-3 py-2 transition-colors ${isSelected ? 'bg-blue-50/50' : 'hover:bg-slate-50/80'}`}
                                >
                                    <div onClick={(e) => e.stopPropagation()} className="shrink-0 flex items-center">
                                        <Checkbox
                                            size="xs"
                                            checked={isSelected}
                                            onChange={(event) => {
                                                if (event.currentTarget.checked) {
                                                    setSelectedIds(prev => [...prev, person.id]);
                                                } else {
                                                    setSelectedIds(prev => prev.filter(id => id !== person.id));
                                                }
                                            }}
                                        />
                                    </div>

                                    <Link
                                        to={`${PERSONS_PATH}/${person.id}`}
                                        className="flex-1 min-w-0 flex items-center justify-between gap-3 group"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <ShowPerson
                                                person={person}
                                                className="text-xs font-semibold text-slate-800 group-hover:text-blue-600 transition-colors"
                                                className2="text-xs font-normal text-slate-400 italic"
                                            />
                                        </div>

                                        <div className="flex items-center gap-2 shrink-0">
                                            {docStr && (
                                                <span className="inline-flex items-center gap-1 text-[11px] text-slate-600 bg-slate-100/90 px-1.5 py-0.5 rounded border border-slate-200/60 font-mono">
                                                    <IconId size={11} className="text-slate-400 shrink-0" />
                                                    <span>{docStr}</span>
                                                </span>
                                            )}

                                            {person.country_code && (
                                                <span className="text-[11px] text-slate-500 hidden sm:inline">
                                                    {t(`countries.${person.country_code}`)}
                                                </span>
                                            )}

                                            {person.tags && person.tags.length > 0 && (
                                                <div className="hidden md:flex flex-wrap gap-1">
                                                    {person.tags.map((tag, index) => (
                                                        <Badge key={index} size="xs" color={tagColor(tag)}>
                                                            {t(tag)}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            )}

                                            <span className="text-[11px] font-mono text-slate-400 w-12 text-right">
                                                #{person.id}
                                            </span>
                                        </div>
                                    </Link>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
